// Module-level state for lazy initialization
let registrationPromise = null;
/**
 * Internal function to register the service worker.
 * Only called once when first needed.
 */
const initializeServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
        try {
            await navigator.serviceWorker.register('/sw.js', {
                scope: '/',
            });
            return navigator.serviceWorker.ready;
        }
        catch (error) {
            console.error('Service worker registration failed:', error);
            return null;
        }
    }
    return null;
};
/**
 * Ensures service worker is initialized.
 * Safe to call multiple times - will only initialize once.
 */
const ensureInitialized = () => {
    if (!registrationPromise) {
        registrationPromise = initializeServiceWorker();
    }
};
/**
 * Registers the service worker and returns the registration.
 * Automatically initializes on first call.
 */
export const registerServiceWorker = async () => {
    ensureInitialized();
    return registrationPromise;
};
/**
 * Subscribes to push notifications using the service worker.
 * Automatically initializes service worker if needed.
 */
export const subscribeToPush = async (vapidKey) => {
    ensureInitialized();
    const registration = await registrationPromise;
    if (!registration) {
        throw new Error('Service worker not available');
    }
    return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
    });
};
/**
 * Gets the current push subscription if one exists.
 * Automatically initializes service worker if needed.
 */
export const getCurrentPushSubscription = async () => {
    ensureInitialized();
    const registration = await registrationPromise;
    if (!registration) {
        return null;
    }
    return registration.pushManager.getSubscription();
};
/**
 * Removes the existing push subscription if one exists.
 * Automatically initializes service worker if needed.
 */
export const removeExistingSubscription = async () => {
    ensureInitialized();
    const registration = await registrationPromise;
    if (!registration) {
        return false;
    }
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
        return subscription.unsubscribe();
    }
    return true; // No subscription to remove
};
/**
 * Unregisters all service workers for this origin.
 * Clears the module-level registration state.
 * Returns true if unregistration was successful, false otherwise.
 */
export const unregisterServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
        return false;
    }
    try {
        // Get all service worker registrations
        const registrations = await navigator.serviceWorker.getRegistrations();
        // Unregister all registrations
        const unregisterPromises = registrations.map((registration) => registration.unregister());
        const results = await Promise.all(unregisterPromises);
        const allUnregistered = results.every((result) => result === true);
        // Clear the module-level state so service worker can be re-initialized if needed
        registrationPromise = null;
        return allUnregistered;
    }
    catch (error) {
        console.error('Service worker unregistration failed:', error);
        // Clear state even on error
        registrationPromise = null;
        return false;
    }
};
